import React, { useState, useEffect, useCallback, useMemo } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Manual Activations: organizations that never use the Adobe API ("org-01").
 * Resellers add customers as usual; they land here as "To add". The team adds
 * them in the official Adobe console, then marks them done. Customers of a
 * manual org who expire or are deleted land in "To remove".
 */
const box = (bg, border, color) => ({ background: bg, border: `1px solid ${border}`, color, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 13 });

function durationText(u) {
    if (u.durationDays) return `${u.durationDays} day${u.durationDays === 1 ? '' : 's'}`;
    if (u.durationMonths) return `${u.durationMonths} mo`;
    return '—';
}

function ago(iso) {
    if (!iso) return '';
    const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    return h < 48 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}

function downloadCsv(name, rows) {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['email', 'organization', 'reseller', 'duration', 'products', 'added', 'expires'];
    const lines = [head.join(',')].concat(rows.map(u => [u.email, u.organizationName, u.resellerName, durationText(u), (u.products || []).join(' | '), u.activatedAt, u.expiresAt].map(esc).join(',')));
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

function QueueTable({ rows, selected, setSelected, kind }) {
    const allOn = rows.length > 0 && rows.every(r => selected.has(r.rowid));
    const toggle = id => { const s = new Set(selected); s.has(id) ? s.delete(id) : s.add(id); setSelected(s); };
    return (
        <div style={{ maxHeight: 420, overflow: 'auto' }}>
            <table className="data-table">
                <thead>
                    <tr>
                        <th style={{ width: 32 }}><input type="checkbox" checked={allOn} onChange={() => setSelected(allOn ? new Set() : new Set(rows.map(r => r.rowid)))} /></th>
                        <th>Email</th><th>Organization</th><th>Reseller</th><th>Duration</th>
                        <th>{kind === 'add' ? 'Waiting' : 'Removed from records'}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map(u => (
                        <tr key={u.rowid} onClick={() => toggle(u.rowid)} style={{ cursor: 'pointer' }}>
                            <td><input type="checkbox" checked={selected.has(u.rowid)} onChange={() => toggle(u.rowid)} onClick={e => e.stopPropagation()} /></td>
                            <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{u.email}</td>
                            <td>{u.organizationName}</td>
                            <td>{u.resellerName || `#${u.resellerId}`}{u.assignmentType === 'manual_org_import' && <span style={{ marginLeft: 6, fontSize: 11, color: '#64748b' }}>(admin import)</span>}</td>
                            <td>{durationText(u)}</td>
                            <td style={{ fontSize: 12, color: '#64748b' }}>{kind === 'add' ? ago(u.activatedAt) : ago(u.deletedAt)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ManualOrgAdmin({ onCountsChange }) {
    const [data, setData] = useState({ pending: [], toRemove: [] });
    const [settings, setSettings] = useState(null);
    const [selAdd, setSelAdd] = useState(new Set());
    const [selRemove, setSelRemove] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [notice, setNotice] = useState(null);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({ name: '', products: 'Creative Cloud Pro Configuration', userLimit: '' });
    const [filterOrg, setFilterOrg] = useState('all');

    const authFetch = useCallback(async (path, options = {}) => {
        const token = localStorage.getItem('adminToken');
        try {
            const res = await fetch(`${API_URL}${path}`, {
                ...options,
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(options.headers || {}) },
            });
            return { ok: res.ok, body: await res.json().catch(() => ({})) };
        } catch (e) {
            return { ok: false, body: { error: 'Could not reach the server — try again.' } };
        }
    }, []);

    const load = useCallback(async () => {
        const [q, s] = await Promise.all([authFetch('/admin/manual-org/queue'), authFetch('/admin/manual-org/settings')]);
        if (q.ok) {
            setData({ pending: q.body.pending || [], toRemove: q.body.toRemove || [] });
            onCountsChange && onCountsChange({ pending: (q.body.pending || []).length, toRemove: (q.body.toRemove || []).length });
        }
        if (s.ok) setSettings(s.body);
    }, [authFetch, onCountsChange]);

    useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

    const orgNames = useMemo(() => [...new Set(data.pending.concat(data.toRemove).map(u => u.organizationName).filter(Boolean))], [data]);
    const pending = filterOrg === 'all' ? data.pending : data.pending.filter(u => u.organizationName === filterOrg);
    const toRemove = filterOrg === 'all' ? data.toRemove : data.toRemove.filter(u => u.organizationName === filterOrg);
    const pick = (rows, sel) => { const chosen = rows.filter(r => sel.has(r.rowid)); return chosen.length ? chosen : rows; };

    const copy = async (rows, label) => {
        const text = rows.map(r => r.email).join('\n');
        try { await navigator.clipboard.writeText(text); setNotice(`Copied ${rows.length} email(s) ${label}.`); }
        catch (e) { window.prompt('Copy these emails:', text); }
    };

    const act = async (path, body, msg) => {
        setBusy(true); setError(null); setNotice(null);
        const { ok, body: r } = await authFetch(path, { method: 'POST', body: JSON.stringify(body) });
        setBusy(false);
        if (!ok) { setError(r.error || 'Failed.'); return; }
        setNotice(msg(r));
        setSelAdd(new Set()); setSelRemove(new Set());
        load();
    };

    const markDone = () => {
        const rows = data.pending.filter(r => selAdd.has(r.rowid));
        if (!rows.length) { setError('Tick the emails you have added in Adobe first.'); return; }
        if (!window.confirm(`Mark ${rows.length} email(s) as added in the Adobe console?\n\nTheir resellers will see them as Active.`)) return;
        act('/admin/manual-org/mark-done', { rowids: rows.map(r => r.rowid) }, r => `${r.done} marked done — resellers now see them as Active.`);
    };

    const reject = () => {
        const rows = data.pending.filter(r => selAdd.has(r.rowid));
        if (!rows.length) { setError('Tick the emails that could not be added first.'); return; }
        const reason = window.prompt(`Reject ${rows.length} email(s)? They are removed and resellers get their credits back.\n\nReason (optional, shown in the reseller's history):`, '');
        if (reason === null) return;
        act('/admin/manual-org/reject', { rowids: rows.map(r => r.rowid), reason }, r => `${r.rejected} rejected · ${r.refunded} refunded (${r.credits} credits returned).`);
    };

    const markRemoved = () => {
        const rows = data.toRemove.filter(r => selRemove.has(r.rowid));
        if (!rows.length) { setError('Tick the emails you have removed from Adobe first.'); return; }
        if (!window.confirm(`Mark ${rows.length} email(s) as removed from the Adobe console?`)) return;
        act('/admin/manual-org/mark-removed', { rowids: rows.map(r => r.rowid) }, r => `${r.removed} marked removed.`);
    };

    const createOrg = async (e) => {
        e.preventDefault();
        setBusy(true); setError(null); setNotice(null);
        const { ok, body } = await authFetch('/admin/manual-org/orgs', { method: 'POST', body: JSON.stringify(form) });
        setBusy(false);
        if (!ok) { setError(body.error || 'Could not create.'); return; }
        setNotice(body.message);
        setForm({ ...form, name: '', userLimit: '' });
        load();
    };

    const flag = async (org, manual) => {
        if (manual && !window.confirm(`Make "${org.name}" a manual organization?\n\nIts Adobe API sync is turned OFF. New customers added to it are saved as pending and listed here for you to add by hand.`)) return;
        const { ok, body } = await authFetch(`/admin/manual-org/orgs/${org.id}`, { method: 'PATCH', body: JSON.stringify({ manual }) });
        if (!ok) { setError(body.error || 'Failed.'); return; }
        load();
    };

    const manualOrgs = (settings?.orgs || []).filter(o => o.manual);

    return (
        <div>
            {settings && !settings.enabled && (
                <div style={box('#fef2f2', '#fecaca', '#991b1b')}>
                    The feature is <strong>off</strong>. Turn it on in <strong>Settings → Manual Organizations</strong>. While off, manual organizations refuse new customers.
                </div>
            )}
            {notice && <div style={box('#f0fdf4', '#bbf7d0', '#166534')}>{notice}</div>}
            {error && <div style={box('#fef2f2', '#fecaca', '#991b1b')}>{error}</div>}

            <div className="card">
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                    <h3 className="card-title">To add in Adobe <span style={{ color: '#b45309' }}>({data.pending.length})</span></h3>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {orgNames.length > 1 && (
                            <select className="form-input" style={{ width: 'auto' }} value={filterOrg} onChange={e => setFilterOrg(e.target.value)}>
                                <option value="all">All organizations</option>
                                {orgNames.map(n => <option key={n} value={n}>{n}</option>)}
                            </select>
                        )}
                        <button className="btn btn-secondary" onClick={load} disabled={busy}>Refresh</button>
                    </div>
                </div>
                <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>
                    Copy the emails, add them in the official Adobe admin console, then tick them and press <strong>Mark done</strong>.
                    Copy/Export use the ticked rows, or all rows when none are ticked.
                </p>
                {pending.length === 0 ? <div style={{ color: '#64748b', padding: 12 }}>Nothing waiting. 🎉</div> : (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary" onClick={() => copy(pick(pending, selAdd), 'to add')}>Copy emails</button>
                            <button className="btn btn-secondary" onClick={() => downloadCsv(`manual-to-add-${Date.now()}.csv`, pick(pending, selAdd))}>Export CSV</button>
                            <button className="btn btn-primary" onClick={markDone} disabled={busy}>✓ Mark done ({selAdd.size})</button>
                            <button className="btn btn-danger" onClick={reject} disabled={busy}>Reject + refund ({selAdd.size})</button>
                        </div>
                        <QueueTable rows={pending} selected={selAdd} setSelected={setSelAdd} kind="add" />
                    </>
                )}
            </div>

            <div className="card">
                <div className="card-header">
                    <h3 className="card-title">To remove from Adobe <span style={{ color: '#b91c1c' }}>({data.toRemove.length})</span></h3>
                </div>
                <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>
                    Customers of a manual organization who expired or were deleted. Remove them in the Adobe console, then mark them removed.
                </p>
                {toRemove.length === 0 ? <div style={{ color: '#64748b', padding: 12 }}>Nothing to remove.</div> : (
                    <>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                            <button className="btn btn-secondary" onClick={() => copy(pick(toRemove, selRemove), 'to remove')}>Copy emails</button>
                            <button className="btn btn-secondary" onClick={() => downloadCsv(`manual-to-remove-${Date.now()}.csv`, pick(toRemove, selRemove))}>Export CSV</button>
                            <button className="btn btn-primary" onClick={markRemoved} disabled={busy}>✓ Mark removed ({selRemove.size})</button>
                        </div>
                        <QueueTable rows={toRemove} selected={selRemove} setSelected={setSelRemove} kind="remove" />
                    </>
                )}
            </div>

            <div className="card">
                <div className="card-header"><h3 className="card-title">Manual organizations</h3></div>
                <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 12px' }}>
                    A manual organization needs no Adobe API credentials. After creating it, give resellers access in
                    <strong> Bulk Org Access</strong>. To import ready-made accounts into it, use <strong>Bulk Import</strong> and choose a Credits Pool reseller.
                    Users and export work like any organization.
                </p>
                {manualOrgs.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
                        {manualOrgs.map(o => (
                            <span key={o.id} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 999, padding: '4px 10px', fontSize: 13 }}>
                                {o.name} <button type="button" onClick={() => flag(o, false)} title="Stop treating as manual" style={{ border: 0, background: 'none', cursor: 'pointer', color: '#64748b' }}>×</button>
                            </span>
                        ))}
                    </div>
                )}
                <form onSubmit={createOrg} style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', alignItems: 'end' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">New manual organization name</label>
                        <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="org-01" required />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">Products (one per line)</label>
                        <textarea className="form-input" rows={2} value={form.products} onChange={e => setForm({ ...form, products: e.target.value })} required />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                        <label className="form-label">User limit (optional)</label>
                        <input className="form-input" type="number" min="1" value={form.userLimit} onChange={e => setForm({ ...form, userLimit: e.target.value })} placeholder="No limit" />
                    </div>
                    <button className="btn btn-primary" type="submit" disabled={busy}>+ Create</button>
                </form>
                <details style={{ marginTop: 14 }}>
                    <summary style={{ cursor: 'pointer', fontSize: 13, color: '#475569' }}>Make an existing organization manual…</summary>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        {(settings?.orgs || []).filter(o => !o.manual).map(o => (
                            <button key={o.id} type="button" className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => flag(o, true)}>{o.name}</button>
                        ))}
                    </div>
                </details>
            </div>
        </div>
    );
}

export default ManualOrgAdmin;
